import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import axios from 'axios';
import RaisedButton from 'material-ui/RaisedButton';
import copy from 'copy-to-clipboard';
import generateUUID from 'helpers/generateUUID';
import 'monaco-editor';
import { ControlledEditor } from '@monaco-editor/react';

import Popover from 'material-ui/Popover';
import BasicDialog from 'components/Dialogs/BasicDialog';
import SlateDialog from 'components/Shared/SlateDialog/SlateDialog';
import DelayedPreloader from 'components/Shared/DelayedPreloader/DelayedPreloader';
import EmptyState from 'components/Shared/EmptyState/EmptyState';
import palette from 'theme/palette';
import paletteDark from 'theme/paletteHexDark';
import paletteHex from 'theme/paletteHex';
import { getEffectiveTheme } from 'helpers/themeHelper';
import SlateTextInput from 'components/Shared/SlateTextInput/SlateTextInput';
import { VerifyDeleteDialog } from 'components/Shared/VerifyDeleteDialog';
import ScriptsDropdown from './ScriptsDropdown';
import { showNotification } from 'store/actions/notification';
import { openDemoInPane } from 'store/actions/slatePane';
import { openHelpPane } from 'store/actions/helpPane';
import { notificationTypes, api, API_ROOT_URI } from 'config/constants';
import { cloneDeep } from 'lodash';
import { options } from 'helpers/cookieHelper';
import 'css/selector-dropdown.css';
import './style.css';

var DEFAULT_SCRIPT_CONTENT = 'function onSlateLoaded(ctx: SlateContext) {\n\t\n}';

function injectScriptIntoSlate(slateDto, scriptContent) {
  var dto = JSON.parse(JSON.stringify(slateDto));
  if (dto.fields) {
    var fieldKeys = Object.keys(dto.fields);
    for (var i = 0; i < fieldKeys.length; i++) {
      var field = dto.fields[fieldKeys[i]];
      if (field.title === 'script') {
        field.value = scriptContent;
        return dto;
      }
    }
  }
  return dto;
}

export function clearScriptsSessionStorage() {
  try { sessionStorage.removeItem('scriptsState'); } catch (e) { /* ignore */ }
}

class Scripts extends Component {
  constructor(props) {
    super(props);
    var restoredState = {};
    try {
      var saved = sessionStorage.getItem('scriptsState');
      if (saved) {
        var parsed = JSON.parse(saved);
        if (parsed.scripts) restoredState.scripts = parsed.scripts;
        if (parsed.activeScriptIndex !== undefined) restoredState.activeScriptIndex = parsed.activeScriptIndex;
        if (parsed.savedScripts) restoredState.savedScripts = parsed.savedScripts;
        if (parsed.lastTestSlate) restoredState.lastTestSlate = parsed.lastTestSlate;
        if (parsed.isOwnDemo) restoredState.isOwnDemo = parsed.isOwnDemo;
      }
    } catch (e) { /* ignore */ }

    this.state = Object.assign({
      scripts: [],
      activeScriptIndex: null,
      isDropdownOpen: false,
      dropdownAnchorEl: null,
      isEditingName: false,
      isCancelDialogOpen: false,
      isNewScriptDialogOpen: false,
      savedScripts: {},
      isTestDialogOpen: false,
      templates: [],
      selectedTemplateUUID: null,
      isLoadingTemplates: false,
      isGeneratingDemo: false,
      isHotReloading: false,
      isFileMenuOpen: false,
      fileMenuAnchorEl: null,
      isOwnDemo: false,
      lastTestSlate: null,
      isOpenDialogOpen: false,
      openDialogTab: 'stack',
      stackScripts: [],
      isLoadingStackScripts: false,
      selectedStackScriptUUID: null,
      isOpenDirtyDialogOpen: false,
      isOpenFileDragover: false,
      isLoadingOpenScript: false,
      isSaveAsDialogOpen: false,
      saveAsName: '',
      saveAsNameError: null,
      isSaveDialogOpen: false,
      saveStep: null,
      saveError: null,
      saveErrorStep: null,
      saveDiagnostics: [],
      isManageDialogOpen: false,
      manageScripts: [],
      isLoadingManageScripts: false,
      deletingScriptUUID: null,
      isDeletingScript: false,
    }, restoredState);
    this._pushingDemo = false;
    this._saveCancelId = 0;
    this.nameInputRef = React.createRef();
    this.openFileInputRef = React.createRef();
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevState.scripts !== this.state.scripts ||
      prevState.activeScriptIndex !== this.state.activeScriptIndex ||
      prevState.savedScripts !== this.state.savedScripts ||
      prevState.lastTestSlate !== this.state.lastTestSlate ||
      prevState.isOwnDemo !== this.state.isOwnDemo
    ) {
      try {
        sessionStorage.setItem('scriptsState', JSON.stringify({
          scripts: this.state.scripts,
          activeScriptIndex: this.state.activeScriptIndex,
          savedScripts: this.state.savedScripts,
          lastTestSlate: this.state.lastTestSlate,
          isOwnDemo: this.state.isOwnDemo,
        }));
      } catch (e) { /* ignore */ }
    }

    // Ownership detection: track whether Scripts owns the current pane content
    if (prevProps.contentJson !== this.props.contentJson) {
      if (this._pushingDemo) {
        // Scripts initiated this change -- still ours
        this._pushingDemo = false;
      } else if (this.state.isOwnDemo === true && !this.props.contentJson && prevProps.contentJson) {
        // Pane was closed while we owned it -- remember we had a test slate
        this.setState({ isOwnDemo: 'closed' });
      } else if (this.state.isOwnDemo) {
        // Another source changed content -- no longer ours
        this.setState({ isOwnDemo: false });
      }
    }

    // React to theme mode changes for Monaco editor
    if (prevProps.themeMode !== this.props.themeMode) {
      var monaco = window.monaco;
      if (monaco) {
        var effective = getEffectiveTheme(this.props.themeMode);
        monaco.editor.setTheme(effective === 'dark' ? 'slate-dark' : 'vs');
      }
    }
  }

  handleDropdownOpen = function(e) {
    if (e) {
      e.preventDefault();
    }
    this.setState({
      isDropdownOpen: true,
      dropdownAnchorEl: e.currentTarget,
    });
  };

  handleDropdownClose = function() {
    this.setState({
      isDropdownOpen: false,
      dropdownAnchorEl: null,
    });
  };

  handleFileMenuOpen = function(e) {
    if (e) e.preventDefault();
    this.setState({ isFileMenuOpen: true, fileMenuAnchorEl: e.currentTarget });
  };

  handleFileMenuClose = function() {
    this.setState({ isFileMenuOpen: false, fileMenuAnchorEl: null });
  };

  handleScriptClick = function(index) {
    this.setState({ activeScriptIndex: index });
  };

  handleCreateScript = function() {
    if (this.isActiveScriptDirty()) {
      this.setState({ isNewScriptDialogOpen: true });
      return;
    }
    this.handleCreateScriptConfirm();
  };

  handleCreateScriptConfirm = function() {
    var index = this.state.activeScriptIndex;
    var scripts = this.state.scripts.slice();

    // Close current script if dirty (revert or remove)
    if (index !== null) {
      var script = scripts[index];
      var saved = this.state.savedScripts[script.uuid];
      if (!saved) {
        scripts.splice(index, 1);
      } else {
        scripts[index] = Object.assign({}, scripts[index], { name: saved.name, content: saved.content });
      }
    }

    var newScript = {
      uuid: generateUUID(),
      name: 'Untitled Script',
      content: DEFAULT_SCRIPT_CONTENT,
    };
    scripts.push(newScript);
    this.setState({
      scripts: scripts,
      activeScriptIndex: scripts.length - 1,
      isNewScriptDialogOpen: false,
    });
  };

  handleNameChange = function(e) {
    var index = this.state.activeScriptIndex;
    if (index === null) return;
    var scripts = this.state.scripts.slice();
    scripts[index] = Object.assign({}, scripts[index], { name: e.target.value });
    this.setState({ scripts: scripts });
  };

  handleNameEditStart = function() {
    var _this = this;
    this.setState({ isEditingName: true }, function() {
      if (_this.nameInputRef.current) {
        _this.nameInputRef.current.focus();
        _this.nameInputRef.current.select();
      }
    });
  };

  handleNameEditEnd = function() {
    this.setState({ isEditingName: false });
  };

  handleNameKeyDown = function(e) {
    if (e.key === 'Enter') {
      this.setState({ isEditingName: false });
    }
  };

  handleEditorDidMount = function(_getEditorValue, editor) {
    var monaco = window.monaco;
    if (monaco) {
      // Define custom navy dark theme matching dashboard palette
      monaco.editor.defineTheme('slate-dark', {
        base: 'vs-dark',
        inherit: true,
        rules: [
          { token: 'comment', foreground: paletteDark.syntaxComment, fontStyle: 'italic' },
          { token: 'keyword', foreground: paletteDark.syntaxKeyword },
          { token: 'string', foreground: paletteDark.syntaxString },
          { token: 'number', foreground: paletteDark.syntaxNumber },
          { token: 'type', foreground: paletteDark.syntaxType },
        ],
        colors: {
          'editor.background': paletteDark.editorBg,
          'editor.foreground': paletteDark.editorForeground,
          'editor.lineHighlightBackground': paletteDark.editorLineHighlight,
          'editor.selectionBackground': paletteDark.editorSelection,
          'editor.inactiveSelectionBackground': paletteDark.editorInactiveSelection,
          'editorLineNumber.foreground': paletteDark.editorLineNumber,
          'editorLineNumber.activeForeground': paletteDark.editorLineNumberActive,
          'editorCursor.foreground': paletteDark.editorCursor,
          'editor.selectionHighlightBackground': paletteDark.editorSelectionHighlight,
          'editorWidget.background': paletteDark.editorWidgetBg,
          'editorWidget.border': paletteDark.editorWidgetBorder,
          'editorSuggestWidget.background': paletteDark.editorWidgetBg,
          'editorSuggestWidget.border': paletteDark.editorWidgetBorder,
          'editorSuggestWidget.selectedBackground': paletteDark.editorSuggestSelectedBg,
          'editorHoverWidget.background': paletteDark.editorWidgetBg,
          'editorHoverWidget.border': paletteDark.editorWidgetBorder,
          'input.background': paletteDark.editorBg,
          'input.border': paletteDark.editorWidgetBorder,
          'scrollbar.shadow': paletteDark.editorScrollbarShadow,
          'scrollbarSlider.background': paletteDark.editorScrollbarSlider,
          'scrollbarSlider.hoverBackground': paletteDark.editorScrollbarSliderHover,
          'scrollbarSlider.activeBackground': paletteDark.editorScrollbarSliderActive,
        },
      });

      // Apply the custom theme if in dark mode
      if (getEffectiveTheme(this.props.themeMode) === 'dark') {
        monaco.editor.setTheme('slate-dark');
      }

      axios
        .get(API_ROOT_URI + '/scripts/type-definition', options)
        .then(function(res) {
          var typeDefinition = res.data && res.data.data ? res.data.data : '';
          if (typeDefinition) {
            monaco.languages.typescript.typescriptDefaults.addExtraLib(
              typeDefinition,
              'slate-context.d.ts'
            );
          }
        })
        .catch(function() { /* ignore - editor works without types */ });
    }
  };

  handleEditorChange = function(ev, value) {
    var index = this.state.activeScriptIndex;
    if (index === null) return;
    var scripts = this.state.scripts.slice();
    scripts[index] = Object.assign({}, scripts[index], { content: value });
    this.setState({ scripts: scripts });
  };

  handleCopyUuid = function() {
    var index = this.state.activeScriptIndex;
    if (index === null) return;
    var script = this.state.scripts[index];
    copy(script.uuid);
    this.props.showNotification(
      notificationTypes.SUCCESS,
      this.props.t('scripts:copiedToClipboard'),
    );
  };

  isActiveScriptSaveable = function() {
    var index = this.state.activeScriptIndex;
    if (index === null) return false;
    var script = this.state.scripts[index];
    if (!script) return false;
    if (script.stackUUID) return true;
    if (script.name === 'Untitled Script' && script.content === DEFAULT_SCRIPT_CONTENT) return false;
    return true;
  };

  validateScript = function(content) {
    return axios.post(
      api.VALIDATE_SCRIPT,
      { source: content },
      cloneDeep(options)
    ).then(function(res) {
      return res.data.data.validationHash;
    });
  };

  saveScriptToStack = function(uuid, name, content, validationHash) {
    var currentAccount = this.props.currentAccount;
    if (!currentAccount) return Promise.reject();
    return axios.put(
      api.GET_TEMPLATES + '/' + currentAccount.uuid + '/scripts/' + uuid,
      { name: name, source: content, validationHash: validationHash },
      cloneDeep(options)
    );
  };

  handleSaveCancel = function() {
    this._saveCancelId++;
    this.setState({
      isSaveDialogOpen: false,
      isSaveAsDialogOpen: false,
      saveStep: null,
      saveError: null,
      saveErrorStep: null,
      saveDiagnostics: [],
      saveAsName: '',
      saveAsNameError: null,
    });
  };

  handleSave = function() {
    var _this = this;
    var index = this.state.activeScriptIndex;
    if (index === null) return;
    var script = this.state.scripts[index];
    if (!script) return;
    if (!this.isActiveScriptSaveable()) return;

    if (!script.stackUUID) {
      this.handleSaveAs();
      return;
    }

    var cancelId = ++this._saveCancelId;
    this.setState({ isSaveDialogOpen: true, saveStep: 'validating', saveError: null, saveErrorStep: null, saveDiagnostics: [] });

    var savingStartedAt = null;
    this.validateScript(script.content)
      .then(function(validationHash) {
        if (_this._saveCancelId !== cancelId) return;
        savingStartedAt = Date.now();
        _this.setState({ saveStep: 'saving' });
        return _this.saveScriptToStack(script.stackUUID, script.name, script.content, validationHash);
      })
      .then(function() {
        if (_this._saveCancelId !== cancelId) return;
        var elapsed = Date.now() - savingStartedAt;
        var remaining = Math.max(0, 2000 - elapsed);
        setTimeout(function() {
          if (_this._saveCancelId !== cancelId) return;
          var savedScripts = Object.assign({}, _this.state.savedScripts);
          savedScripts[script.uuid] = { name: script.name, content: script.content };
          _this.setState({ isSaveDialogOpen: false, saveStep: null, saveError: null, saveErrorStep: null, saveDiagnostics: [], savedScripts: savedScripts });
          _this.props.showNotification(notificationTypes.SUCCESS, _this.props.t('scripts:saveSuccess'));
        }, remaining);
      })
      .catch(function(err) {
        if (_this._saveCancelId !== cancelId) return;
        var message = _this.props.t('scripts:saveError');
        var errorStep = _this.state.saveStep || 'validating';
        var diagnostics = [];
        if (err && err.response && err.response.status === 422 && err.response.data && err.response.data.validationErrors && err.response.data.validationErrors.dataErrors) {
          var dataErrors = err.response.data.validationErrors.dataErrors;
          if (dataErrors.scriptDiagnostics) {
            try {
              diagnostics = JSON.parse(dataErrors.scriptDiagnostics);
              message = null;
            } catch (e) {
              message = dataErrors.scriptDiagnostics;
            }
          } else {
            var errorKeys = Object.keys(dataErrors);
            if (errorKeys.length > 0) {
              message = dataErrors[errorKeys[0]];
            }
          }
        }
        _this.setState({ saveStep: null, saveError: message, saveErrorStep: errorStep, saveDiagnostics: diagnostics });
      });
  };

  handleSaveAs = function() {
    var index = this.state.activeScriptIndex;
    if (index === null) return;
    var script = this.state.scripts[index];
    if (!script) return;
    this.setState({ isSaveAsDialogOpen: true, saveAsName: script.name, saveAsNameError: null });
  };

  handleSaveAsDialogClose = function() {
    if (this.state.saveStep) return;
    this.setState({ isSaveAsDialogOpen: false, saveAsName: '', saveAsNameError: null, saveError: null, saveErrorStep: null });
  };

  handleSaveAsNameChange = function(e) {
    this.setState({ saveAsName: e.target.value, saveAsNameError: null });
  };

  handleSaveAsConfirm = function() {
    var _this = this;
    var index = this.state.activeScriptIndex;
    if (index === null) return;
    var script = this.state.scripts[index];
    if (!script) return;
    var name = this.state.saveAsName.trim();
    if (!name) return;

    var targetUUID = script.stackUUID ? generateUUID() : script.uuid;
    var cancelId = ++this._saveCancelId;

    this.setState({ saveStep: 'validating', saveError: null, saveErrorStep: null, saveDiagnostics: [] });

    var savingStartedAt = null;
    this.validateScript(script.content)
      .then(function(validationHash) {
        if (_this._saveCancelId !== cancelId) return;
        savingStartedAt = Date.now();
        _this.setState({ saveStep: 'saving' });
        return _this.saveScriptToStack(targetUUID, name, script.content, validationHash);
      })
      .then(function() {
        if (_this._saveCancelId !== cancelId) return;
        var elapsed = Date.now() - savingStartedAt;
        var remaining = Math.max(0, 2000 - elapsed);
        setTimeout(function() {
          if (_this._saveCancelId !== cancelId) return;
          var scripts = _this.state.scripts.slice();
          var savedScripts = Object.assign({}, _this.state.savedScripts);
          var updatedScript = Object.assign({}, scripts[index], { uuid: targetUUID, name: name, stackUUID: targetUUID });
          if (scripts[index].uuid !== targetUUID) {
            delete savedScripts[scripts[index].uuid];
          }
          scripts[index] = updatedScript;
          savedScripts[targetUUID] = { name: name, content: script.content };
          _this.setState({
            scripts: scripts,
            savedScripts: savedScripts,
            isSaveAsDialogOpen: false,
            saveAsName: '',
            saveAsNameError: null,
            saveStep: null,
            saveError: null,
            saveErrorStep: null,
            saveDiagnostics: [],
          });
          _this.props.showNotification(notificationTypes.SUCCESS, _this.props.t('scripts:saveSuccess'));
        }, remaining);
      })
      .catch(function(err) {
        if (_this._saveCancelId !== cancelId) return;
        var message = _this.props.t('scripts:saveError');
        var errorStep = _this.state.saveStep || 'validating';
        var diagnostics = [];
        if (err && err.response && err.response.status === 422 && err.response.data && err.response.data.validationErrors && err.response.data.validationErrors.dataErrors) {
          var dataErrors = err.response.data.validationErrors.dataErrors;
          if (dataErrors.scriptDiagnostics) {
            try {
              diagnostics = JSON.parse(dataErrors.scriptDiagnostics);
              message = null;
            } catch (e) {
              message = dataErrors.scriptDiagnostics;
            }
          } else if (dataErrors.name) {
            message = dataErrors.name;
          } else if (dataErrors.source) {
            message = dataErrors.source;
          } else {
            var errorKeys = Object.keys(dataErrors);
            if (errorKeys.length > 0) {
              message = dataErrors[errorKeys[0]];
            }
          }
        }
        _this.setState({ saveStep: null, saveError: message, saveErrorStep: errorStep, saveDiagnostics: diagnostics });
      });
  };

  isActiveScriptDirty = function() {
    var index = this.state.activeScriptIndex;
    if (index === null) return false;
    var script = this.state.scripts[index];
    var saved = this.state.savedScripts[script.uuid];
    if (!saved) {
      // New script still in initial state is not dirty
      if (script.name === 'Untitled Script' && script.content === DEFAULT_SCRIPT_CONTENT) return false;
      return true;
    }
    // Local-only scripts (loaded from computer, never saved to stack) are
    // always considered dirty because replacing them loses the content.
    if (!script.stackUUID) return true;
    return saved.name !== script.name || saved.content !== script.content;
  };

  handleCloseScript = function() {
    if (this.isActiveScriptDirty()) {
      this.setState({ isCancelDialogOpen: true });
    } else {
      this.handleCloseScriptConfirm();
    }
  };

  handleCancelDialogClose = function() {
    this.setState({ isCancelDialogOpen: false });
  };

  handleTestClick = function() {
    this.setState({ isTestDialogOpen: true });
    this.fetchTemplates();
  };

  fetchTemplates = function() {
    var _this = this;
    var currentAccount = this.props.currentAccount;
    if (!currentAccount) return;

    _this.setState({ isLoadingTemplates: true });

    var allTemplates = [];
    var fetchOptions = cloneDeep(options);

    function fetchPage() {
      axios
        .get(api.GET_TEMPLATES + '/' + currentAccount.uuid + '/templates', fetchOptions)
        .then(function(res) {
          var responseData = res.data && res.data.data ? res.data.data : {};
          var entities = responseData.entities ? responseData.entities : [];
          var continuationToken = responseData.continuationToken ? responseData.continuationToken : '';
          allTemplates = allTemplates.concat(entities);

          if (continuationToken) {
            fetchOptions.headers['Continuation-Token'] = continuationToken;
            fetchPage();
          } else {
            _this.setState({ templates: allTemplates, isLoadingTemplates: false });
          }
        })
        .catch(function() {
          _this.setState({ templates: [], isLoadingTemplates: false });
        });
    }

    fetchPage();
  };

  handleTemplateSelect = function(uuid) {
    this.setState({ selectedTemplateUUID: uuid });
  };

  handleRunTest = function() {
    var _this = this;
    var currentAccount = this.props.currentAccount;
    var selectedTemplateUUID = this.state.selectedTemplateUUID;
    var activeScript = this.state.activeScriptIndex !== null
      ? this.state.scripts[this.state.activeScriptIndex]
      : null;

    if (!currentAccount || !selectedTemplateUUID || !activeScript) return;

    _this.setState({ isGeneratingDemo: true });

    axios
      .post(
        api.GET_TEMPLATES + '/' + currentAccount.uuid + '/scripts/build-test-slate',
        { templateUUID: selectedTemplateUUID, userScript: activeScript.content },
        options
      )
      .then(function(res) {
        var slateDto = res.data && res.data.data ? res.data.data : res.data;

        _this._pushingDemo = true;
        _this.props.openDemoInPane(slateDto);
        _this.setState({
          isTestDialogOpen: false,
          isGeneratingDemo: false,
          selectedTemplateUUID: null,
          isOwnDemo: true,
          lastTestSlate: JSON.parse(JSON.stringify(slateDto)),
        });
      })
      .catch(function(err) {
        _this.setState({ isGeneratingDemo: false });
        var validationErrors = err && err.response && err.response.data
          && err.response.data.validationErrors && err.response.data.validationErrors.dataErrors;
        if (validationErrors) {
          var messages = Object.keys(validationErrors).map(function(key) { return validationErrors[key]; });
          _this.props.showNotification(
            notificationTypes.ERROR,
            messages.join('. ')
          );
        } else {
          _this.props.showNotification(
            notificationTypes.ERROR,
            _this.props.t('scripts:testError')
          );
        }
      });
  };

  handleRerunTest = function() {
    var activeScript = this.state.activeScriptIndex !== null
      ? this.state.scripts[this.state.activeScriptIndex]
      : null;
    if (!activeScript || !this.state.isOwnDemo) return;

    var sourceSlate = this.props.contentJson || this.state.lastTestSlate;
    if (!sourceSlate) return;
    var modified = injectScriptIntoSlate(sourceSlate, activeScript.content);

    this._pushingDemo = true;
    this.props.openDemoInPane(modified);
    this.setState({ isOwnDemo: true });
  };

  handleHotReload = function() {
    var _this = this;
    var activeScript = this.state.activeScriptIndex !== null
      ? this.state.scripts[this.state.activeScriptIndex]
      : null;
    if (!activeScript || (!this.state.isOwnDemo)) return;

    var latestSlate = this.props.liveContentData || this.props.contentJson || this.state.lastTestSlate;
    if (!latestSlate) return;

    _this.setState({ isHotReloading: true });

    axios
      .post(
        API_ROOT_URI + '/scripts/prepare-for-test-slate',
        { userScript: activeScript.content },
        options
      )
      .then(function(res) {
        var preparedScript = res.data && res.data.data ? res.data.data : res.data;
        var modified = injectScriptIntoSlate(latestSlate, preparedScript);

        _this._pushingDemo = true;
        _this.props.openDemoInPane(modified);
        _this.setState({
          isHotReloading: false,
          isOwnDemo: true,
          lastTestSlate: JSON.parse(JSON.stringify(modified)),
        });
      })
      .catch(function(err) {
        _this.setState({ isHotReloading: false });
        _this.props.showNotification(
          notificationTypes.ERROR,
          _this.props.t('scripts:testError')
        );
      });
  };

  handleShowSlatePane = function() {
    if (this.state.lastTestSlate) {

      this._pushingDemo = true;
      this.props.openDemoInPane(this.state.lastTestSlate);
      this.setState({ isOwnDemo: true });
    }
  };

  handleTestDialogClose = function() {
    this.setState({
      isTestDialogOpen: false,
      selectedTemplateUUID: null,
    });
  };

  handleOpenClick = function() {
    if (this.isActiveScriptDirty()) {
      this.setState({ isOpenDirtyDialogOpen: true });
      return;
    }
    this.handleOpenDialogShow();
  };

  handleOpenDialogShow = function() {
    this.setState({
      isOpenDialogOpen: true,
      isOpenDirtyDialogOpen: false,
      openDialogTab: 'stack',
      selectedStackScriptUUID: null,
      isOpenFileDragover: false,
    });
    this.fetchStackScripts();
  };

  handleOpenDialogClose = function() {
    this.setState({
      isOpenDialogOpen: false,
      stackScripts: [],
      selectedStackScriptUUID: null,
      isOpenFileDragover: false,
    });
  };

  handleOpenTabChange = function(tab) {
    this.setState({ openDialogTab: tab });
  };

  fetchStackScripts = function() {
    var _this = this;
    var currentAccount = this.props.currentAccount;
    if (!currentAccount) return;

    _this.setState({ isLoadingStackScripts: true });

    var allScripts = [];
    var fetchOptions = cloneDeep(options);

    function fetchPage() {
      axios
        .get(api.GET_TEMPLATES + '/' + currentAccount.uuid + '/scripts', fetchOptions)
        .then(function(res) {
          var responseData = res.data && res.data.data ? res.data.data : {};
          var entities = Array.isArray(responseData) ? responseData : (responseData.entities ? responseData.entities : []);
          var continuationToken = Array.isArray(responseData) ? '' : (responseData.continuationToken ? responseData.continuationToken : '');
          allScripts = allScripts.concat(entities);

          if (continuationToken) {
            fetchOptions.headers['Continuation-Token'] = continuationToken;
            fetchPage();
          } else {
            _this.setState({ stackScripts: allScripts, isLoadingStackScripts: false });
          }
        })
        .catch(function() {
          _this.setState({ stackScripts: [], isLoadingStackScripts: false });
        });
    }

    fetchPage();
  };

  handleStackScriptSelect = function(uuid) {
    this.setState({ selectedStackScriptUUID: uuid });
  };

  handleManageDialogShow = function() {
    this.setState({ isManageDialogOpen: true, manageScripts: [], deletingScriptUUID: null, isDeletingScript: false });
    this.fetchManageScripts();
  };

  handleManageDialogClose = function() {
    this.setState({ isManageDialogOpen: false, manageScripts: [], deletingScriptUUID: null, isDeletingScript: false });
  };

  fetchManageScripts = function() {
    var _this = this;
    var currentAccount = this.props.currentAccount;
    if (!currentAccount) return;

    _this.setState({ isLoadingManageScripts: true });

    var allScripts = [];
    var fetchOptions = cloneDeep(options);

    function fetchPage() {
      axios
        .get(api.GET_TEMPLATES + '/' + currentAccount.uuid + '/scripts', fetchOptions)
        .then(function(res) {
          var responseData = res.data && res.data.data ? res.data.data : {};
          var entities = Array.isArray(responseData) ? responseData : (responseData.entities ? responseData.entities : []);
          var continuationToken = Array.isArray(responseData) ? '' : (responseData.continuationToken ? responseData.continuationToken : '');
          allScripts = allScripts.concat(entities);

          if (continuationToken) {
            fetchOptions.headers['Continuation-Token'] = continuationToken;
            fetchPage();
          } else {
            _this.setState({ manageScripts: allScripts, isLoadingManageScripts: false });
          }
        })
        .catch(function() {
          _this.setState({ manageScripts: [], isLoadingManageScripts: false });
        });
    }

    fetchPage();
  };

  handleDeleteScriptClick = function(scriptUUID) {
    this.setState({ deletingScriptUUID: scriptUUID });
  };

  handleDeleteScriptCancel = function() {
    this.setState({ deletingScriptUUID: null, isDeletingScript: false });
  };

  handleDeleteScriptConfirm = function() {
    var _this = this;
    var scriptUUID = this.state.deletingScriptUUID;
    if (!scriptUUID) return;
    var currentAccount = this.props.currentAccount;
    if (!currentAccount) return;

    this.setState({ isDeletingScript: true });

    axios
      .delete(
        api.GET_TEMPLATES + '/' + currentAccount.uuid + '/scripts/' + scriptUUID,
        cloneDeep(options)
      )
      .then(function() {
        var manageScripts = _this.state.manageScripts.filter(function(s) {
          return s.uuid !== scriptUUID;
        });
        _this.setState({ manageScripts: manageScripts, deletingScriptUUID: null, isDeletingScript: false });
        _this.props.showNotification(notificationTypes.SUCCESS, _this.props.t('scripts:deleteSuccess'));
      })
      .catch(function() {
        _this.setState({ isDeletingScript: false });
        _this.props.showNotification(notificationTypes.ERROR, _this.props.t('scripts:deleteError'));
      });
  };

  handleOpenStackScript = function() {
    var _this = this;
    var selected = this.state.selectedStackScriptUUID;
    if (!selected) return;
    var script = null;
    for (var i = 0; i < this.state.stackScripts.length; i++) {
      if (this.state.stackScripts[i].uuid === selected) {
        script = this.state.stackScripts[i];
        break;
      }
    }
    if (!script) return;
    var currentAccount = this.props.currentAccount;
    if (!currentAccount) return;

    this.setState({ isLoadingOpenScript: true });
    axios.get(
      api.GET_TEMPLATES + '/' + currentAccount.uuid + '/scripts/' + script.uuid,
      cloneDeep(options)
    ).then(function(res) {
      var data = res.data.data || res.data;
      _this.setState({ isLoadingOpenScript: false });
      _this.handleOpenScriptFinalize({
        uuid: script.uuid,
        name: data.name || script.name || 'Untitled Script',
        content: data.source || '',
        stackUUID: script.uuid,
      });
    }).catch(function() {
      _this.setState({ isLoadingOpenScript: false });
      _this.props.showNotification(notificationTypes.ERROR, _this.props.t('scripts:openScriptError'));
    });
  };

  handleOpenFileDragOver = function(e) {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ isOpenFileDragover: true });
  };

  handleOpenFileDragLeave = function(e) {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ isOpenFileDragover: false });
  };

  handleOpenFileDrop = function(e) {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ isOpenFileDragover: false });
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      this.handleComputerFileSelect(e.dataTransfer.files[0]);
    }
  };

  handleComputerFileInputChange = function(e) {
    if (e.target.files && e.target.files.length > 0) {
      this.handleComputerFileSelect(e.target.files[0]);
      e.target.value = '';
    }
  };

  handleComputerFileSelect = function(file) {
    var _this = this;
    var reader = new FileReader();
    reader.onload = function(e) {
      var content = e.target.result;
      var name = file.name || 'Untitled Script';
      var dotIndex = name.lastIndexOf('.');
      if (dotIndex > 0) {
        name = name.substring(0, dotIndex);
      }
      _this.handleOpenScriptFinalize({
        uuid: generateUUID(),
        name: name,
        content: content,
      });
    };
    reader.onerror = function() {
      _this.props.showNotification(
        notificationTypes.ERROR,
        _this.props.t('scripts:openFileError')
      );
    };
    reader.readAsText(file);
  };

  handleOpenScriptFinalize = function(scriptData) {
    var index = this.state.activeScriptIndex;
    var scripts = this.state.scripts.slice();
    var savedScripts = Object.assign({}, this.state.savedScripts);

    // Remove current active script if it exists
    if (index !== null && scripts[index]) {
      var current = scripts[index];
      var saved = savedScripts[current.uuid];
      if (!saved) {
        scripts.splice(index, 1);
      } else {
        scripts[index] = Object.assign({}, scripts[index], { name: saved.name, content: saved.content });
      }
    }

    var newScript = { uuid: scriptData.uuid, name: scriptData.name, content: scriptData.content };
    if (scriptData.stackUUID) {
      newScript.stackUUID = scriptData.stackUUID;
    }
    scripts.push(newScript);
    savedScripts[scriptData.uuid] = { name: scriptData.name, content: scriptData.content };

    this.setState({
      scripts: scripts,
      activeScriptIndex: scripts.length - 1,
      savedScripts: savedScripts,
      isOpenDialogOpen: false,
      stackScripts: [],
      selectedStackScriptUUID: null,
      isOpenFileDragover: false,
    });
  };

  handleCloseScriptConfirm = function() {
    var index = this.state.activeScriptIndex;
    if (index === null) return;
    var script = this.state.scripts[index];
    var saved = this.state.savedScripts[script.uuid];

    if (!saved) {
      // New script -- remove it entirely
      var scripts = this.state.scripts.slice();
      scripts.splice(index, 1);
      this.setState({
        scripts: scripts,
        activeScriptIndex: null,
        isCancelDialogOpen: false,
      });
    } else {
      // Existing script -- revert to saved state and deselect
      var reverted = this.state.scripts.slice();
      reverted[index] = Object.assign({}, reverted[index], { name: saved.name, content: saved.content });
      this.setState({
        scripts: reverted,
        activeScriptIndex: null,
        isCancelDialogOpen: false,
      });
    }
  };

  renderSaveProgress = function() {
    var t = this.props.t;
    var saveStep = this.state.saveStep;
    var saveError = this.state.saveError;
    var saveErrorStep = this.state.saveErrorStep;
    var saveDiagnostics = this.state.saveDiagnostics;
    var hasError = saveError || saveDiagnostics.length > 0;

    var step1Class = 'scripts-save-step';
    var step1IconClass = 'material-symbols-outlined scripts-save-step-icon';
    var step1Icon = 'radio_button_unchecked';
    var step2Class = 'scripts-save-step';
    var step2IconClass = 'material-symbols-outlined scripts-save-step-icon';
    var step2Icon = 'radio_button_unchecked';

    if (saveStep === 'validating') {
      step1Class += ' scripts-save-step--active';
      step1IconClass += ' scripts-save-step-icon--active scripts-save-step-icon--spin';
      step1Icon = 'sync';
      step2Class += ' scripts-save-step--pending';
    } else if (saveStep === 'saving') {
      step1Class += ' scripts-save-step--done';
      step1IconClass += ' scripts-save-step-icon--done';
      step1Icon = 'check_circle';
      step2Class += ' scripts-save-step--active';
      step2IconClass += ' scripts-save-step-icon--active scripts-save-step-icon--spin';
      step2Icon = 'sync';
    } else if (hasError && saveErrorStep === 'validating') {
      step1Class += ' scripts-save-step--error';
      step1IconClass += ' scripts-save-step-icon--error';
      step1Icon = 'error';
      step2Class += ' scripts-save-step--pending';
    } else if (hasError && saveErrorStep === 'saving') {
      step1Class += ' scripts-save-step--done';
      step1IconClass += ' scripts-save-step-icon--done';
      step1Icon = 'check_circle';
      step2Class += ' scripts-save-step--error';
      step2IconClass += ' scripts-save-step-icon--error';
      step2Icon = 'error';
    }

    return (
      <div className="scripts-save-progress">
        {saveStep && (
          <div className="scripts-save-progress-hint">{t('scripts:saveProgressHint')}</div>
        )}
        <div className="scripts-save-steps">
          <div className={step1Class}>
            <span className={step1IconClass}>{step1Icon}</span>
            <span>{t('scripts:validatingStep')}</span>
          </div>
          <div className="scripts-save-step-connector" />
          <div className={step2Class}>
            <span className={step2IconClass}>{step2Icon}</span>
            <span>{t('scripts:savingStep')}</span>
          </div>
        </div>
        {saveDiagnostics.length > 0 ? (
          <div className="scripts-save-diagnostics">
            <div className="scripts-save-diagnostics-header">
              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>error</span>
              <span>{t('scripts:validationErrors')}</span>
            </div>
            <div className="scripts-save-diagnostic-list">
              {saveDiagnostics.map(function(diag, i) {
                return (
                  <div key={i} className="scripts-save-diagnostic-item">
                    <span className="scripts-save-diagnostic-line">Line {diag.startLineNumber}</span>
                    <span className="scripts-save-diagnostic-message">{diag.message}</span>
                  </div>
                );
              })}
            </div>
          </div>
        ) : saveError ? (
          <div className="scripts-save-error">{saveError}</div>
        ) : null}
      </div>
    );
  };

  render() {
    var _this = this;
    var t = this.props.t;
    var scripts = this.state.scripts;
    var activeScriptIndex = this.state.activeScriptIndex;
    var isDropdownOpen = this.state.isDropdownOpen;
    var dropdownAnchorEl = this.state.dropdownAnchorEl;

    var activeScript = activeScriptIndex !== null && scripts[activeScriptIndex]
      ? scripts[activeScriptIndex]
      : null;

    var hasScripts = scripts.length > 0;

    return (
      <div className="inner-page scripts-page">
        <div className="inner-page-header">
          <div className="inner-page-title">
            <h1>{t('routes:scripts')}</h1>
          </div>
          <button
            type="button"
            className="scripts-help-btn"
            onClick={function () { _this.props.openHelpPane('scripts'); }}
          >
            <span className="material-symbols-outlined scripts-help-btn-icon">help</span>
            <span className="scripts-help-btn-label">Help</span>
          </button>
        </div>

        <div className="scripts-content">
          <div className="scripts-editor-container">
            <div className="scripts-toolbar">
              <div className="scripts-toolbar-left">
                <button
                  type="button"
                  className="scripts-toolbar-btn"
                  onClick={function(e) { _this.handleFileMenuOpen(e); }}
                >
                  <span className="scripts-toolbar-btn-label">File</span>
                  <span className="material-symbols-outlined scripts-toolbar-btn-chevron">keyboard_arrow_down</span>
                </button>
              </div>
              <div className="scripts-toolbar-center"></div>
              <div className="scripts-toolbar-right">
                {activeScript && (this.state.isOwnDemo === true ? (
                  <>
                    <button
                      type="button"
                      className="scripts-toolbar-btn"
                      onClick={function() { _this.handleTestClick(); }}
                    >
                      <span className="material-symbols-outlined scripts-toolbar-btn-icon">play_arrow</span>
                      <span className="scripts-toolbar-btn-label">{t('scripts:newTestSlate')}</span>
                    </button>
                    <span className="scripts-toolbar-divider" />
                    <button
                      type="button"
                      className="scripts-toolbar-btn"
                      onClick={function() { _this.handleHotReload(); }}
                      disabled={_this.state.isHotReloading}
                    >
                      <span className={'material-symbols-outlined scripts-toolbar-btn-icon' + (_this.state.isHotReloading ? ' scripts-test-btn-icon-spin' : '')}>refresh</span>
                      <span className="scripts-toolbar-btn-label">{t('scripts:hotReload')}</span>
                    </button>
                  </>
                ) : this.state.isOwnDemo === 'closed' ? (
                  <>
                    <span className="scripts-toolbar-mobile-only">
                      <button
                        type="button"
                        className="scripts-toolbar-btn"
                        onClick={function() { _this.handleShowSlatePane(); }}
                      >
                        <span className="material-symbols-outlined scripts-toolbar-btn-icon">visibility</span>
                        <span className="scripts-toolbar-btn-label">{t('scripts:showSlate')}</span>
                      </button>
                      <span className="scripts-toolbar-divider" />
                      <button
                        type="button"
                        className="scripts-toolbar-btn"
                        onClick={function() { _this.handleHotReload(); }}
                        disabled={_this.state.isHotReloading}
                      >
                        <span className={'material-symbols-outlined scripts-toolbar-btn-icon' + (_this.state.isHotReloading ? ' scripts-test-btn-icon-spin' : '')}>refresh</span>
                        <span className="scripts-toolbar-btn-label">{t('scripts:hotReload')}</span>
                      </button>
                      <span className="scripts-toolbar-divider" />
                    </span>
                    <button
                      type="button"
                      className="scripts-toolbar-btn"
                      onClick={function() { _this.handleTestClick(); }}
                    >
                      <span className="material-symbols-outlined scripts-toolbar-btn-icon">play_arrow</span>
                      <span className="scripts-toolbar-btn-label">{t('scripts:testScript')}</span>
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="scripts-toolbar-btn"
                    onClick={function() { _this.handleTestClick(); }}
                  >
                    <span className="material-symbols-outlined scripts-toolbar-btn-icon">play_arrow</span>
                    <span className="scripts-toolbar-btn-label">{t('scripts:testScript')}</span>
                  </button>
                ))}
              </div>
            </div>
            {activeScript ? (
              <>
                <div className="scripts-info-strip">
                  <div className="scripts-info-strip-left">
                    <span className="scripts-info-strip-name">{activeScript.name}</span>
                  </div>
                  <div className="scripts-info-strip-right">
                    <span className="scripts-info-strip-uuid-label">{t('scripts:scriptUuid')}</span>
                    <span className="scripts-info-strip-uuid-value">{activeScript.uuid}</span>
                    <button
                      type="button"
                      className="scripts-info-strip-copy"
                      onClick={function() { _this.handleCopyUuid(); }}
                    >
                      <span className="material-symbols-outlined">content_copy</span>
                    </button>
                  </div>
                </div>
                <div className="scripts-editor">
                  <ControlledEditor
                    height="100%"
                    language="typescript"
                    theme={getEffectiveTheme(this.props.themeMode) === 'dark' ? 'slate-dark' : 'vs'}
                    loading={<div style={{ width: '100%', height: '100%', background: getEffectiveTheme(this.props.themeMode) === 'dark' ? paletteDark.editorBg : paletteHex.white }} />}
                    value={activeScript.content}
                    editorDidMount={function(getValue, editor) { _this.handleEditorDidMount(getValue, editor); }}
                    onChange={function(ev, value) { _this.handleEditorChange(ev, value); }}
                    options={{
                      minimap: { enabled: false },
                      fontSize: 12,
                      lineNumbers: 'on',
                      scrollBeyondLastLine: false,
                      automaticLayout: true,
                      tabSize: 2,
                      fixedOverflowWidgets: true,
                      scrollbar: { horizontal: 'auto', horizontalScrollbarSize: 8 },
                    }}
                  />
                </div>
              </>
            ) : hasScripts ? (
              <div className="scripts-empty-state-area">
                <EmptyState
                  icon="code"
                  title={t('scripts:noScriptOpenTitle')}
                  description={t('scripts:noScriptOpenDescription')}
                />
              </div>
            ) : (
              <div className="scripts-empty-state-area">
                <EmptyState
                  icon="code"
                  title={t('scripts:getStartedTitle')}
                  description={t('scripts:getStartedDescription')}
                />
              </div>
            )}
          </div>

          <Popover
            open={this.state.isFileMenuOpen}
            anchorEl={this.state.fileMenuAnchorEl}
            onRequestClose={function() { _this.handleFileMenuClose(); }}
            anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
            targetOrigin={{ horizontal: 'left', vertical: 'top' }}
            className="selector-dropdown-popover scripts-file-menu-popover"
            zDepth={0}
            style={{
              marginTop: 4,
              background: 'transparent',
              boxShadow: 'none',
              overflow: 'visible',
              borderRadius: 10,
            }}
          >
            <div className="selector-dropdown scripts-file-menu" style={{ minWidth: 180 }}>
              <div className="selector-dropdown-content">
                <button
                  className="scripts-file-menu-item"
                  onClick={function() { _this.handleFileMenuClose(); _this.handleCreateScript(); }}
                >
                  <span className="material-symbols-outlined scripts-file-menu-item-icon">add</span>
                  <span className="scripts-file-menu-item-label">New</span>
                </button>
                <button
                  className="scripts-file-menu-item"
                  onClick={function() { _this.handleFileMenuClose(); _this.handleOpenClick(); }}
                >
                  <span className="material-symbols-outlined scripts-file-menu-item-icon">folder_open</span>
                  <span className="scripts-file-menu-item-label">Open</span>
                </button>
                <button
                  className="scripts-file-menu-item"
                  onClick={function() { _this.handleFileMenuClose(); _this.handleManageDialogShow(); }}
                >
                  <span className="material-symbols-outlined scripts-file-menu-item-icon">settings</span>
                  <span className="scripts-file-menu-item-label">{t('scripts:manageScripts')}</span>
                </button>
                <div className="scripts-file-menu-divider" />
                <button
                  className={'scripts-file-menu-item' + (!_this.isActiveScriptSaveable() ? ' scripts-file-menu-item-disabled' : '')}
                  onClick={function() { if (!_this.isActiveScriptSaveable()) return; _this.handleFileMenuClose(); _this.handleSave(); }}
                  disabled={!_this.isActiveScriptSaveable()}
                >
                  <span className="material-symbols-outlined scripts-file-menu-item-icon">save</span>
                  <span className="scripts-file-menu-item-label">Save</span>
                </button>
                <button
                  className={'scripts-file-menu-item' + (!_this.isActiveScriptSaveable() ? ' scripts-file-menu-item-disabled' : '')}
                  onClick={function() { if (!_this.isActiveScriptSaveable()) return; _this.handleFileMenuClose(); _this.handleSaveAs(); }}
                  disabled={!_this.isActiveScriptSaveable()}
                >
                  <span className="material-symbols-outlined scripts-file-menu-item-icon">save_as</span>
                  <span className="scripts-file-menu-item-label">Save As</span>
                </button>
                <div className="scripts-file-menu-divider" />
                <button
                  className={'scripts-file-menu-item' + (!activeScript ? ' scripts-file-menu-item-disabled' : '')}
                  onClick={function() { if (!activeScript) return; _this.handleFileMenuClose(); _this.handleCloseScript(); }}
                  disabled={!activeScript}
                >
                  <span className="material-symbols-outlined scripts-file-menu-item-icon">close</span>
                  <span className="scripts-file-menu-item-label">Close</span>
                </button>
              </div>
            </div>
          </Popover>
        </div>

        <ScriptsDropdown
          open={isDropdownOpen}
          anchorEl={dropdownAnchorEl}
          onClose={function() { _this.handleDropdownClose(); }}
          scripts={scripts}
          activeIndex={activeScriptIndex}
          onScriptClick={function(index) { _this.handleScriptClick(index); }}
          onCreateClick={function() { _this.handleCreateScript(); }}
        />

        <BasicDialog
          open={this.state.isCancelDialogOpen}
          onClose={function() { _this.handleCancelDialogClose(); }}
          title={t('scripts:discardChangesTitle')}
          maxWidth="xs"
          actions={[
            <RaisedButton
              key="keep"
              label={t('scripts:keepEditing')}
              onClick={function() { _this.handleCancelDialogClose(); }}
              style={{ marginRight: 10 }}
            />,
            <RaisedButton
              key="discard"
              label={t('scripts:discardChanges')}
              backgroundColor={palette.brandOrange}
              labelColor={palette.white}
              onClick={function() { _this.handleCloseScriptConfirm(); }}
            />,
          ]}
        >
          <div style={{ padding: '20px 24px', textAlign: 'left' }}>
            {t('scripts:discardChangesMessage')}
          </div>
        </BasicDialog>

        <BasicDialog
          open={this.state.isNewScriptDialogOpen}
          onClose={function() { _this.setState({ isNewScriptDialogOpen: false }); }}
          title={t('scripts:unsavedChangesTitle')}
          maxWidth="xs"
          actions={[
            <RaisedButton
              key="cancel"
              label={t('scripts:keepEditing')}
              onClick={function() { _this.setState({ isNewScriptDialogOpen: false }); }}
              style={{ marginRight: 10 }}
            />,
            <RaisedButton
              key="confirm"
              label={t('scripts:discardAndCreate')}
              backgroundColor={palette.brandOrange}
              labelColor={palette.white}
              onClick={function() { _this.handleCreateScriptConfirm(); }}
            />,
          ]}
        >
          <div style={{ padding: '20px 24px', textAlign: 'left' }}>
            {t('scripts:unsavedChangesNewMessage')}
          </div>
        </BasicDialog>

        <BasicDialog
          open={this.state.isOpenDirtyDialogOpen}
          onClose={function() { _this.setState({ isOpenDirtyDialogOpen: false }); }}
          title={t('scripts:unsavedChangesTitle')}
          maxWidth="xs"
          actions={[
            <RaisedButton
              key="keep"
              label={t('scripts:keepEditing')}
              onClick={function() { _this.setState({ isOpenDirtyDialogOpen: false }); }}
              style={{ marginRight: 10 }}
            />,
            <RaisedButton
              key="discard"
              label={t('scripts:discardAndOpen')}
              backgroundColor={palette.brandOrange}
              labelColor={palette.white}
              onClick={function() { _this.handleOpenDialogShow(); }}
            />,
          ]}
        >
          <div style={{ padding: '20px 24px', textAlign: 'left' }}>
            {t('scripts:unsavedChangesOpenMessage')}
          </div>
        </BasicDialog>

        <SlateDialog
          open={this.state.isSaveDialogOpen}
          onClose={function() { if (!_this.state.saveStep) _this.handleSaveCancel(); }}
          title={this.state.saveStep ? t('scripts:savingTitle') : t('scripts:saveScript')}
          maxWidth="xs"
          closable={!this.state.saveStep}
          actions={this.state.saveStep ? [
            { label: t('btn:cancel'), variant: 'secondary', onClick: function() { _this.handleSaveCancel(); } },
          ] : (this.state.saveError || this.state.saveDiagnostics.length > 0) ? [
            { label: t('btn:close'), variant: 'secondary', onClick: function() { _this.handleSaveCancel(); } },
          ] : []}
        >
          {this.renderSaveProgress()}
        </SlateDialog>

        <SlateDialog
          open={this.state.isSaveAsDialogOpen}
          onClose={function() { if (!_this.state.saveStep) _this.handleSaveAsDialogClose(); }}
          title={this.state.saveStep ? t('scripts:savingTitle') : t('scripts:saveAs')}
          maxWidth="xs"
          closable={!this.state.saveStep}
          actions={this.state.saveStep ? [
            { label: t('btn:cancel'), variant: 'secondary', onClick: function() { _this.handleSaveCancel(); } },
          ] : (this.state.saveError || this.state.saveDiagnostics.length > 0) ? [
            { label: t('btn:close'), variant: 'secondary', onClick: function() { _this.handleSaveCancel(); } },
          ] : [
            { label: t('btn:cancel'), variant: 'secondary', onClick: function() { _this.handleSaveAsDialogClose(); } },
            { label: t('scripts:saveScript'), variant: 'primary', onClick: function() { _this.handleSaveAsConfirm(); }, disabled: !_this.state.saveAsName.trim() },
          ]}
        >
          {this.state.saveStep || this.state.saveError || this.state.saveDiagnostics.length > 0 ? (
            this.renderSaveProgress()
          ) : (
            <div style={{ paddingTop: 16 }}>
              <SlateTextInput
                name="saveAsName"
                label={t('scripts:scriptName')}
                value={this.state.saveAsName}
                onChange={function(e) { _this.handleSaveAsNameChange(e); }}
                error={this.state.saveAsNameError}
                autoFocus
                fullWidth
              />
            </div>
          )}
        </SlateDialog>

        <SlateDialog
          open={this.state.isOpenDialogOpen}
          onClose={function() { _this.handleOpenDialogClose(); }}
          title={t('scripts:openScript')}
          maxWidth="sm"
          contentPadding={false}
          actions={this.state.openDialogTab === 'stack' ? [
            { label: t('btn:cancel'), variant: 'secondary', onClick: function() { _this.handleOpenDialogClose(); } },
            { label: t('btn:open'), variant: 'primary', onClick: function() { _this.handleOpenStackScript(); }, disabled: !this.state.selectedStackScriptUUID || this.state.isLoadingOpenScript, loading: this.state.isLoadingOpenScript },
          ] : [
            { label: t('btn:cancel'), variant: 'secondary', onClick: function() { _this.handleOpenDialogClose(); } },
          ]}
        >
          <div className="scripts-open-tabs">
            <button
              type="button"
              className={'scripts-open-tab' + (this.state.openDialogTab === 'stack' ? ' scripts-open-tab--active' : '')}
              onClick={function() { _this.handleOpenTabChange('stack'); }}
            >
              <span className="material-symbols-outlined scripts-open-tab-icon">cloud</span>
              {t('scripts:openTabStack')}
            </button>
            <button
              type="button"
              className={'scripts-open-tab' + (this.state.openDialogTab === 'computer' ? ' scripts-open-tab--active' : '')}
              onClick={function() { _this.handleOpenTabChange('computer'); }}
            >
              <span className="material-symbols-outlined scripts-open-tab-icon">computer</span>
              {t('scripts:openTabComputer')}
            </button>
          </div>

          <div className="scripts-open-body">
            {this.state.openDialogTab === 'stack' ? (
              this.state.isLoadingStackScripts ? (
                <DelayedPreloader isLoading={this.state.isLoadingStackScripts} isGray />
              ) : this.state.stackScripts.length === 0 ? (
                <div className="scripts-test-empty">
                  <div className="scripts-test-empty-illustration">
                    <div className="scripts-test-empty-icon">
                      <span className="material-symbols-outlined">cloud_off</span>
                    </div>
                    <div className="scripts-test-empty-ripple scripts-test-empty-ripple-1" />
                    <div className="scripts-test-empty-ripple scripts-test-empty-ripple-2" />
                  </div>
                  <h3 className="scripts-test-empty-title">{t('scripts:openStackEmpty')}</h3>
                  <p className="scripts-test-empty-description">{t('scripts:openStackEmptyDescription')}</p>
                </div>
              ) : (
                <div className="scripts-test-list" style={{ maxHeight: 'none' }}>
                  {this.state.stackScripts.map(function(script) {
                    var isSelected = _this.state.selectedStackScriptUUID === script.uuid;
                    return (
                      <div
                        key={script.uuid}
                        className={'scripts-test-item' + (isSelected ? ' scripts-test-item-selected' : '')}
                        onClick={function() { _this.handleStackScriptSelect(script.uuid); }}
                      >
                        <span className={'scripts-test-item-radio' + (isSelected ? ' scripts-test-item-radio-checked' : '')}>
                          {isSelected && <span className="scripts-test-item-radio-dot" />}
                        </span>
                        <span className="scripts-test-item-label">{script.name || script.title || script.uuid}</span>
                        {script.modifiedDate && (
                          <span className="scripts-open-item-date">{new Date(script.modifiedDate).toLocaleDateString()}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )
            ) : (
              <div
                className={'scripts-open-computer' + (this.state.isOpenFileDragover ? ' scripts-open-computer--dragover' : '')}
                onDragOver={function(e) { _this.handleOpenFileDragOver(e); }}
                onDragLeave={function(e) { _this.handleOpenFileDragLeave(e); }}
                onDrop={function(e) { _this.handleOpenFileDrop(e); }}
              >
                <input
                  ref={this.openFileInputRef}
                  type="file"
                  accept=".js,.ts,.txt"
                  style={{ display: 'none' }}
                  onChange={function(e) { _this.handleComputerFileInputChange(e); }}
                />
                <span className="material-symbols-outlined scripts-open-computer-icon">upload_file</span>
                <p className="scripts-open-computer-description">{t('scripts:openComputerDescription')}</p>
                <button
                  type="button"
                  className="scripts-open-computer-browse"
                  onClick={function() { _this.openFileInputRef.current && _this.openFileInputRef.current.click(); }}
                >
                  {t('scripts:openComputerBrowse')}
                </button>
                <p className="scripts-open-computer-hint">{t('scripts:openComputerHint')}</p>
              </div>
            )}
          </div>
        </SlateDialog>

        <SlateDialog
          open={this.state.isTestDialogOpen}
          onClose={function() { _this.handleTestDialogClose(); }}
          title={t('scripts:selectTemplateTitle')}
          maxWidth="xs"
          contentPadding={false}
          actions={[
            { label: t('btn:cancel'), variant: 'secondary', onClick: function() { _this.handleTestDialogClose(); } },
            { label: t('scripts:runTest'), variant: 'primary', onClick: function() { _this.handleRunTest(); }, disabled: !this.state.selectedTemplateUUID, loading: this.state.isGeneratingDemo },
          ]}
        >
          {this.state.isLoadingTemplates ? (
            <DelayedPreloader isLoading={this.state.isLoadingTemplates} isGray />
          ) : this.state.templates.length === 0 ? (
            <div className="scripts-test-empty">
              <div className="scripts-test-empty-illustration">
                <div className="scripts-test-empty-icon">
                  <span className="material-symbols-outlined">dashboard</span>
                </div>
                <div className="scripts-test-empty-ripple scripts-test-empty-ripple-1" />
                <div className="scripts-test-empty-ripple scripts-test-empty-ripple-2" />
              </div>
              <h3 className="scripts-test-empty-title">{t('scripts:noTemplatesTitle')}</h3>
              <p className="scripts-test-empty-description">{t('scripts:noTemplatesDescription')}</p>
            </div>
          ) : (
            <>
              <p className="scripts-test-description">{t('scripts:selectTemplateDescription')}</p>
              <div className="scripts-test-list">
                {this.state.templates.map(function(tmpl) {
                  var isSelected = _this.state.selectedTemplateUUID === tmpl.uuid;
                  return (
                    <div
                      key={tmpl.uuid}
                      className={'scripts-test-item' + (isSelected ? ' scripts-test-item-selected' : '')}
                      onClick={function() { _this.handleTemplateSelect(tmpl.uuid); }}
                    >
                      <span className={'scripts-test-item-radio' + (isSelected ? ' scripts-test-item-radio-checked' : '')}>
                        {isSelected && <span className="scripts-test-item-radio-dot" />}
                      </span>
                      <span className="scripts-test-item-label">{tmpl.title || tmpl.uuid}</span>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </SlateDialog>

        <SlateDialog
          open={this.state.isManageDialogOpen}
          onClose={function() { _this.handleManageDialogClose(); }}
          title={t('scripts:manageScripts')}
          maxWidth="xs"
          contentPadding={false}
          actions={[
            { label: t('btn:close'), variant: 'secondary', onClick: function() { _this.handleManageDialogClose(); } },
          ]}
        >
          <div className="scripts-manage-body">
            {this.state.isLoadingManageScripts ? (
              <DelayedPreloader isLoading={this.state.isLoadingManageScripts} isGray />
            ) : this.state.manageScripts.length === 0 ? (
              <div className="scripts-test-empty">
                <div className="scripts-test-empty-illustration">
                  <div className="scripts-test-empty-icon">
                    <span className="material-symbols-outlined">cloud_off</span>
                  </div>
                  <div className="scripts-test-empty-ripple scripts-test-empty-ripple-1" />
                  <div className="scripts-test-empty-ripple scripts-test-empty-ripple-2" />
                </div>
                <h3 className="scripts-test-empty-title">{t('scripts:manageEmpty')}</h3>
                <p className="scripts-test-empty-description">{t('scripts:manageEmptyDescription')}</p>
              </div>
            ) : (
              <div className="scripts-manage-list">
                {this.state.manageScripts.map(function(script) {
                  return (
                    <div key={script.uuid} className="scripts-manage-item">
                      <div className="scripts-manage-item-icon-wrap">
                        <span className="material-symbols-outlined">description</span>
                      </div>
                      <div className="scripts-manage-item-info">
                        <span className="scripts-manage-item-name">{script.name || script.title || script.uuid}</span>
                        {script.modifiedDate && (
                          <span className="scripts-manage-item-date">{new Date(script.modifiedDate).toLocaleDateString()}</span>
                        )}
                      </div>
                      <button
                        type="button"
                        className="scripts-manage-action"
                        onClick={function() { _this.handleDeleteScriptClick(script.uuid); }}
                      >
                        <span className="material-symbols-outlined">delete</span>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </SlateDialog>

        <VerifyDeleteDialog
          open={!!this.state.deletingScriptUUID}
          onClose={function() { _this.handleDeleteScriptCancel(); }}
          onConfirm={function() { _this.handleDeleteScriptConfirm(); }}
          itemName={(function() {
            var name = '';
            if (_this.state.deletingScriptUUID) {
              for (var i = 0; i < _this.state.manageScripts.length; i++) {
                if (_this.state.manageScripts[i].uuid === _this.state.deletingScriptUUID) {
                  name = _this.state.manageScripts[i].name || _this.state.manageScripts[i].title || _this.state.manageScripts[i].uuid;
                  break;
                }
              }
            }
            return name;
          })()}
          itemType={t('scripts:scriptItemType')}
          title={t('scripts:deleteScriptTitle')}
          message={t('scripts:deleteScriptMessage', { name: (function() {
            var name = '';
            if (_this.state.deletingScriptUUID) {
              for (var i = 0; i < _this.state.manageScripts.length; i++) {
                if (_this.state.manageScripts[i].uuid === _this.state.deletingScriptUUID) {
                  name = _this.state.manageScripts[i].name || _this.state.manageScripts[i].title || _this.state.manageScripts[i].uuid;
                  break;
                }
              }
            }
            return name;
          })() })}
          isFetching={this.state.isDeletingScript}
        />
      </div>
    );
  }
}

var mapStateToProps = function (state) {
  return {
    currentAccount: state.accounts.currentAccount,
    contentJson: state.slatePane.contentJson,
    liveContentData: state.slatePane.liveContentData,
    themeMode: state.app.themeMode,
  };
};

var mapDispatchToProps = {
  showNotification: showNotification,
  openDemoInPane: openDemoInPane,
  openHelpPane: openHelpPane,
};

export default withTranslation()(
  connect(mapStateToProps, mapDispatchToProps)(Scripts),
);
